import pathToRegexp from 'path-to-regexp';
import { useCallback, useMemo } from 'react';
import { devWarning } from '@tanium/coreui-utils';
import { SideNavItem, SideNavType } from '../types';

// takes route params (e.g. '/params/foo/:fooId') into account when matching
const isRouteParamsAwareMatch = (potentialMatch: string, urlToMatch: string) => {
  const re = pathToRegexp(potentialMatch);
  return re.test(urlToMatch);
};

export const useIsCurrent = ({ navItems, currentUrl }: { navItems: SideNavItem[]; currentUrl: string }) => {
  const { fullUrlToIndicesMap, prefixesMap } = useMemo(() => {
    const currFullUrlToIndicesMap = new Map<string, number[]>();
    const currPrefixesMap = new Map<string, number[]>();

    for (let i = 0; i < navItems.length; i += 1) {
      const sideNavItem = navItems[i];
      if (sideNavItem.type === SideNavType.Home) {
        currFullUrlToIndicesMap.set('/', [i]);
        currFullUrlToIndicesMap.set('/home', [i]);
        currFullUrlToIndicesMap.set('/account-history', [i]);
        currFullUrlToIndicesMap.set('/other-services', [i]);
        currPrefixesMap.set('/home', [i]);
      }
      if (sideNavItem.type === SideNavType.Cloud) {
        currFullUrlToIndicesMap.set('/instance', [i]);
        currPrefixesMap.set('/instance', [i]);
      }
      if (sideNavItem.type === SideNavType.Artifacts) {
        currFullUrlToIndicesMap.set('/artifacts', [i]);
        currPrefixesMap.set('/artifacts', [i]);
      }
    }
    return { fullUrlToIndicesMap: currFullUrlToIndicesMap, prefixesMap: currPrefixesMap };
  }, [navItems]);

  const currentUrlMatchIndices = useMemo(() => {
    // use exact match if present
    if (fullUrlToIndicesMap.get(currentUrl)) {
      return fullUrlToIndicesMap.get(currentUrl);
    }

    // ignore query params at this point
    const [pathName, queryString] = currentUrl.split('?');

    // do exact match without query params
    if (queryString && fullUrlToIndicesMap.get(pathName)) {
      return fullUrlToIndicesMap.get(pathName);
    }

    // get first match using dynamic route params
    const routeParamsMatchUrl = Array.from(fullUrlToIndicesMap.keys()).find((leafUrl: string) =>
      isRouteParamsAwareMatch(leafUrl, pathName)
    );
    if (routeParamsMatchUrl) {
      return fullUrlToIndicesMap.get(routeParamsMatchUrl);
    }

    // no match, use route prefix "catch all"
    const [, moduleName] = pathName.split('/');
    const catchAllMatch = moduleName && prefixesMap.get(`/${moduleName}`);

    devWarning(!!catchAllMatch, 'No match found for console nav highlighting based on current url.');
    return catchAllMatch;
  }, [currentUrl, fullUrlToIndicesMap, prefixesMap]);

  const isCurrent = useCallback(
    (navItemIndex: number) => {
      return !!currentUrlMatchIndices && currentUrlMatchIndices.indexOf(navItemIndex) >= 0;
    },
    [currentUrlMatchIndices]
  );

  return useMemo(
    () => ({
      isCurrent,
      // export for testing purposes
      currentUrlMatchIndices,
    }),
    [currentUrlMatchIndices, isCurrent]
  );
};
